import React, { useState, useEffect } from 'react';
import {
    Upload, Image as ImageIcon, Trash2, Link,
    Copy, ExternalLink, RefreshCw, Check
} from 'lucide-react';
import { callPublicApi } from '../../api'; // API Import
import './MedyaYonetim.css';

const MedyaYonetim = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    // --- FETCH REAL DATA (ROBUST) ---
    useEffect(() => {
        const fetchMedia = async () => {
            setLoading(true);
            try {
                // Safe fetch helper to prevent one failure from breaking all
                const safeFetch = async (endpoint, name) => {
                    try {
                        const res = await callPublicApi(endpoint);
                        // Handle Lambda Proxy "body" if present
                        if (res && res.body && typeof res.body === 'string') {
                            try {
                                const parsed = JSON.parse(res.body);
                                return parsed.items ? parsed : { items: parsed };
                            } catch (e) { return res; }
                        }
                        return res;
                    } catch (e) {
                        console.warn(`${name} yüklenemedi (404/500):`, e);
                        return { items: [] }; // Return empty result so Promise.all succeeds
                    }
                };

                // Parallel fetch with error suppression
                const [events, news, recipes] = await Promise.all([
                    safeFetch('/etkinlikler', 'Etkinlikler'),
                    safeFetch('/haberler', 'Haberler'),
                    safeFetch('/recipes', 'Tarifler') // FIXED: Correct endpoint from /tarifler to /recipes
                ]);

                let mediaList = [];
                let counter = 1;

                // Helper to normalize data items
                const getItems = (data) => {
                    if (!data) return [];
                    if (Array.isArray(data)) return data;
                    if (data.items && Array.isArray(data.items)) return data.items;
                    return [];
                };

                // 1. Etkinlik Görselleri
                getItems(events).forEach(e => {
                    if (e.gorselUrl && isValidUrl(e.gorselUrl)) {
                        mediaList.push({
                            id: `evt-${e.id || counter++}`,
                            url: e.gorselUrl,
                            name: e.baslik || 'Etkinlik Görseli',
                            source: 'Etkinlik',
                            size: 'Linked',
                            date: e.tarih ? new Date(e.tarih).toLocaleDateString() : 'Tarihsiz'
                        });
                    }
                });

                // 2. Haber Görselleri
                getItems(news).forEach(n => {
                    if (n.resimUrl && isValidUrl(n.resimUrl)) {
                        mediaList.push({
                            id: `news-${n.id || counter++}`,
                            url: n.resimUrl,
                            name: n.baslik || 'Haber Görseli',
                            source: 'Haber',
                            size: 'Linked',
                            date: n.olusturmaTarihi ? new Date(n.olusturmaTarihi).toLocaleDateString() : '-'
                        });
                    }
                });

                // 3. Tarif Görselleri
                getItems(recipes).forEach(r => {
                    if (r.resimUrl && isValidUrl(r.resimUrl)) {
                        mediaList.push({
                            id: `rcp-${r.id || counter++}`,
                            url: r.resimUrl,
                            name: r.ad || 'Tarif Görseli',
                            source: 'Tarif',
                            size: 'Linked',
                            date: '-'
                        });
                    }
                });

                setImages(mediaList);

            } catch (e) {
                console.error("Medya verileri derlenirken hata:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchMedia();
    }, []);

    const isValidUrl = (string) => {
        // Simply check if it's a non-empty string. 
        // We rely on the <img> onError to handle actual broken links.
        return string && typeof string === 'string' && string.trim().length > 0;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = () => {
        setDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setDragging(false);
        const files = [...e.dataTransfer.files];
        handleFiles(files);
    };

    const handleFileSelect = (e) => {
        const files = [...e.target.files];
        handleFiles(files);
    };

    const handleFiles = async (files) => {
        if (files.length === 0) return;
        setUploading(true);

        for (const file of files) {
            await new Promise(r => setTimeout(r, 800));
            const reader = new FileReader();
            reader.onload = (e) => {
                const newImage = {
                    id: crypto.randomUUID(),
                    url: e.target.result,
                    name: file.name,
                    source: 'Yüklenen',
                    size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                    date: new Date().toLocaleDateString()
                };
                setImages(prev => [newImage, ...prev]);
            };
            reader.readAsDataURL(file);
        }
        setUploading(false);
    };

    const copyToClipboard = (url, id) => {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const deleteImage = (id) => {
        // Real deletion logic would be here
        if (window.confirm('Bu görsel listeden kaldırılacak (Kaynak içerik etkilenmez).')) {
            setImages(prev => prev.filter(img => img.id !== id));
        }
    };

    return (
        <div className="media-container">
            <div className="media-header">
                <div>
                    <h1>Medya Kütüphanesi</h1>
                    <p>Etkinlik, Haber ve Tariflerde kullanılan tüm görseller.</p>
                </div>
                <div className="storage-info">
                    <ImageIcon size={16} />
                    <span>Toplam: <strong>{images.length}</strong> Görsel</span>
                </div>
            </div>

            {/* UPLOAD AREA */}
            <div
                className={`upload-area ${dragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="upload-content">
                    <div className="icon-circle">
                        {uploading ? <RefreshCw className="spin" size={32} /> : <Upload size={32} />}
                    </div>
                    <h3>
                        {uploading ? 'Yükleniyor...' : 'Yeni Görsel Ekle'}
                    </h3>
                    <p>Sürükleyip bırakın (Geçici Önizleme)</p>
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        disabled={uploading}
                        id="fileInput"
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="fileInput" className="browse-btn">
                        Dosya Seç
                    </label>
                </div>
            </div>

            {/* GALLERY GRID */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Görseller taranıyor...</div>
            ) : (
                <div className="gallery-grid">
                    {images.map(img => (
                        <div key={img.id} className="gallery-item">
                            <div className="image-preview">
                                <img src={img.url} alt={img.name} onError={(e) => e.target.src = 'https://via.placeholder.com/300?text=Gorsel+Hata'} />
                                <div className="overlay">
                                    <button onClick={() => window.open(img.url, '_blank')} title="Önizle">
                                        <ExternalLink size={18} />
                                    </button>
                                    {img.source === 'Yüklenen' && (
                                        <button onClick={() => deleteImage(img.id)} className="delete-btn" title="Sil">
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="image-info">
                                <div className="info-text">
                                    <span className="name" title={img.name}>{img.name}</span>
                                    <span className="meta" style={{ color: img.source === 'Yüklenen' ? '#2563eb' : '#9ca3af' }}>
                                        {img.source} • {img.date}
                                    </span>
                                </div>
                                <button
                                    className={`copy-btn ${copiedId === img.id ? 'copied' : ''}`}
                                    onClick={() => copyToClipboard(img.url, img.id)}
                                    title="Link Kopyala"
                                >
                                    {copiedId === img.id ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                    ))}
                    {images.length === 0 && !loading && (
                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '3rem', color: '#9ca3af', border: '1px dashed #e5e7eb', borderRadius: '12px' }}>
                            Henüz hiç görsel bulunmuyor. Bir etkinlik veya tarif eklediğinizde görseli buraya düşer.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MedyaYonetim;
